/**
 * Главный экран: карта во весь экран и одна плавающая панель поверх неё.
 * На компьютере панель стоит слева, на телефоне — шторкой снизу.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type Device, type EventRow, type Identity, type Position, type Zone, api } from './api'
import { BlockButton } from './BlockButton'
import { Companies } from './Companies'
import { Events } from './Events'
import { History } from './History'
import { Manage } from './Manage'
import { Address } from './Address'
import { EtaButton } from './EtaButton'
import { Profile } from './Profile'
import { RouteButton } from './RouteButton'
import { RefreshButton } from './RefreshButton'
import { ShareButton } from './ShareButton'
import { FleetMap } from './FleetMap'
import { FleetReport } from './FleetReport'
import { eventText } from './eventName'
import { clock, dateTime, num } from './format'
import type { Inset } from './follow'
import { brandOf } from './brand'
import { describe as describeVehicle, matches, passportOf, title } from './vehicleId'
import { ART } from './brandArt'
import { BellIcon, BuildingIcon, ChartIcon, GearIcon, TruckIcon } from './icons'
import { dict, type Lang } from './i18n'
import { Gate, show } from './notify'
import {
  type Detent,
  type Detents,
  detentFor,
  nextDetent,
  projectRelease,
  shouldDragSheet,
  withResistance,
} from './sheet'
import { useFleet } from './useFleet'
import type { Theme } from './theme'
import {
  chargingState,
  energyLevel,
  fuelVolume,
  ignitionOn,
  isBlocked,
  odometerKm,
  rpm,
  speedKmh,
  statusOf,
  tankLiters,
  tankMissing,
  voltage,
} from './vehicle'

type Tab = 'fleet' | 'events' | 'reports' | 'manage' | 'companies'

type Props = {
  identity: Identity
  lang: Lang
  center: { lat: number; lon: number }
  /** Заведён ли ключ Google: без него нет ни адресов, ни времени в пути. */
  maps: boolean
  /** Предел глубины трека в сутках — приходит с сервера. */
  trackDays: number
  /** Тема оформления: показывается и меняется в профиле. */
  theme: Theme
  onTheme: (theme: Theme) => void
  onSignedOut: () => void
  /** Сведения о себе поменялись — например, включили вход в два шага. */
  onIdentity: (identity: Identity) => void
}

export function Fleet({
  identity,
  lang,
  center,
  maps,
  trackDays,
  theme,
  onTheme,
  onSignedOut,
  onIdentity,
}: Props) {
  const t = dict(lang)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  // Счётчик выборов: растёт на каждом нажатии, даже если нажали ту же машину.
  // По нему карта возвращается к ней после того, как её увели рукой, — сам
  // `selectedId` при повторном нажатии не меняется и картой не движет.
  const [focusSeq, setFocusSeq] = useState(0)
  // Шторка на телефоне: её положение (0 — свёрнута, 1 — среднее, 2 — раскрыта)
  // нужно не для вида — на нём висят стили панели, в том числе запрет
  // прокрутки списка в свёрнутом виде.
  const { sheetRef, panelRef, cycle, raise, inset } = useSheet(selectedId)
  const [tab, setTab] = useState<Tab>('fleet')
  const [track, setTrack] = useState<Position[] | null>(null)
  const [zones, setZones] = useState<Zone[]>([])
  const [query, setQuery] = useState('')
  const [profileOpen, setProfileOpen] = useState(false)
  const [routeOpen, setRouteOpen] = useState(false)

  // Выбор машины — в одном месте: список, значок на карте и всё остальное
  // ведут карту одинаково.
  const select = useCallback(
    (id: number) => {
      // Шторка поднимается до того, как карта успеет спросить, сколько
      // экрана закрыто: иначе машина встала бы в середину ещё свободной
      // части и тут же ушла под приехавшую шторку.
      raise()
      setSelectedId(id)
      setFocusSeq((n) => n + 1)
    },
    [raise],
  )

  // Точку для новой зоны выбирают нажатием на карту, а карта живёт здесь —
  // поэтому состоянием выбора владеет главный экран, а не панель управления.
  const [pickingPoint, setPickingPoint] = useState(false)
  const [pickedPoint, setPickedPoint] = useState<{
    latitude: number
    longitude: number
  } | null>(null)

  const loadZones = useCallback(() => {
    api
      .zones()
      .then(setZones)
      .catch(() => {
        // Зоны — украшение карты, а не её содержание: без них она работает.
      })
  }, [])

  useEffect(() => {
    loadZones()
  }, [loadZones])

  // Шлюз живёт всё время, пока открыт экран: он помнит недавно показанное и
  // гасит дребезг на границе зоны. Пересоздать его — значит снова показать
  // всё то же самое.
  const gate = useRef(new Gate())
  // Имена машин нужны уведомлению, но по ним нельзя пересоздавать обработчик:
  // список меняется каждые несколько секунд, а лента рвалась бы вместе с ним.
  const namesRef = useRef(new Map<number, string>())

  const announce = useCallback(
    (events: EventRow[]) => {
      for (const event of events) {
        if (!gate.current.admit(event)) continue
        const who = namesRef.current.get(event.deviceId) ?? `#${event.deviceId}`
        show(who, eventText(event, t))
      }
    },
    [t.eventTypes],
  )

  // Сессия могла истечь, пока вкладка была открыта — это не сбой,
  // а повод показать форму входа.
  const { devices, positions, failed, tails, reload } = useFleet(onSignedOut, announce)

  // Имена для уведомлений — те же госномера, что и на экране: всплывающее
  // окно про машину, которую человек не узнаёт по имени, бесполезно.
  namesRef.current = useMemo(
    () => new Map(devices.map((d) => [d.id, title(d)])),
    [devices],
  )

  // Вкладка управления существует только для администратора. «Компании» —
  // ступенью выше: её видит только администратор сервера (`serverAdmin`),
  // а не любой, у кого есть `canManage`, — иначе администратор одной фирмы
  // видел бы список всех фирм на сервере. Настоящая проверка — на сервере,
  // здесь лишь то, чтобы не показывать дверь, за которой заведомо откажут.
  // 'companies' виден только серверному администратору; serverAdmin всегда
  // влечёт canManage, поэтому вложенность (сначала canManage, потом
  // serverAdmin) безопасна — ветка serverAdmin недостижима не пройдя первую.
  const tabs: Tab[] = identity.canManage
    ? identity.serverAdmin
      ? ['fleet', 'events', 'reports', 'manage', 'companies']
      : ['fleet', 'events', 'reports', 'manage']
    : ['fleet', 'events', 'reports']

  const tabName: Record<Tab, string> = {
    fleet: t.fleet,
    events: t.events,
    reports: t.reports,
    manage: t.manage,
    companies: t.companies,
  }

  const tabIcon: Record<Tab, React.ReactNode> = {
    fleet: <TruckIcon />,
    events: <BellIcon />,
    reports: <ChartIcon />,
    manage: <GearIcon />,
    companies: <BuildingIcon />,
  }

  // Поиск идёт по всему паспорту сразу: номеру, марке, модели и имени.
  // Человек набирает то, что помнит, и не должен угадывать, по какому полю
  // тут принято искать.
  const found = useMemo(
    () => devices.filter((device) => matches(device, query)),
    [devices, query],
  )

  const selected = useMemo(
    () => devices.find((d) => d.id === selectedId) ?? null,
    [devices, selectedId],
  )

  const statusLabel = {
    move: t.moving,
    park: t.parked,
    block: t.blocked,
    quiet: t.quiet,
  } as const

  // «Нет свежих данных с …» — с датой: у молчащего дольше суток одно время
  // суток врало бы. Слов «нет связи» здесь нет (решение владельца 28.09).
  const stateText = (status: keyof typeof statusLabel, device: Device): string =>
    status === 'quiet' && device.lastUpdate
      ? t.quietSince(dateTime(device.lastUpdate, lang))
      : statusLabel[status]

  // Отступ под панелью: по нему карта подбирает масштаб при первом показе,
  // чтобы парк лёг в свободную часть, а не под панель.
  //
  // 346 — настоящий правый край панели: отступ 16 плюс ширина 330 из стилей.
  // Прежние 380 были на глаз, и парк вставал в рамку шире панели. Замерять
  // живьём тут нельзя: это первая отрисовка, панели ещё нет; живой замер
  // делает inset() при перелёте к машине.
  //
  // На телефоне отступ снизу берётся по свёрнутой шторке: это её обычное
  // состояние, а под раскрытую машину всё равно не поместить.
  const insetForWidth = useCallback(
    () =>
      window.innerWidth >= 860
        ? { left: 346, bottom: 60 }
        : { left: 40, bottom: 190 },
    [],
  )
  const [panelInset, setPanelInset] = useState(insetForWidth)

  // Окно успевает измениться до первых машин: страницу открыли в узком окне и
  // тут же развернули, планшет повернули на загрузке. Тогда парк подгонялся бы
  // под панель, которой на этой ширине уже нет. Новый объект — только когда
  // числа и правда другие: иначе карта пересобирала бы все значки на каждое
  // движение края окна.
  useEffect(() => {
    function measure() {
      const свежий = insetForWidth()
      setPanelInset((было) =>
        было.left === свежий.left && было.bottom === свежий.bottom ? было : свежий,
      )
    }
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [insetForWidth])

  async function signOut() {
    try {
      await api.logout()
    } finally {
      onSignedOut()
    }
  }

  return (
    <>
      <FleetMap
        devices={devices}
        positions={positions}
        selectedId={selectedId}
        onSelect={select}
        focusSeq={focusSeq}
        inset={inset}
        lang={lang}
        center={center}
        panelInset={panelInset}
        track={track}
        zones={zones}
        tail={track ? null : selectedId ? (tails.get(selectedId) ?? null) : null}
        picking={pickingPoint}
        onPick={(latitude, longitude) => {
          setPickedPoint({ latitude, longitude })
          setPickingPoint(false)
        }}
      />

      {/* Пока ждём нажатия по карте, панель уезжает: на телефоне она
          закрывает как раз ту часть карты, куда надо ткнуть. */}
      {pickingPoint && (
        <div className="pick-bar" role="status">
          <span>{t.mng.pickOnMap}</span>
          <button onClick={() => setPickingPoint(false)}>{t.confirmNo}</button>
        </div>
      )}

      {/* Шторка на телефоне: три положения и протяжка пальцем, вся работа —
          в useSheet ниже. Ездит обёртка, а не панель: у панели свой сдвиг,
          которым она уходит с дороги, пока на карте выбирают точку.
          На широком экране обёртка ничего не делает: панель стоит слева. */}
      <div className="sheet" ref={sheetRef}>
        {/* data-detent сюда не ставится: им распоряжается useSheet, и два
            хозяина у одного атрибута кончились бы тем, что перерисовка
            возвращала бы шторку в свёрнутое положение. */}
        <div className={'panel' + (pickingPoint ? ' away' : '')} ref={panelRef}>
          {/* Ручка не только показывает, что шторку можно тянуть: по нажатию
              она переводит её в следующее положение. Тянуть удобно не всем. */}
          <button
            className="grabber"
            type="button"
            aria-label={t.sheetHandle}
            onClick={cycle}
          />
        {selected ? (
          <>
            <div className="card-top">
              <button
                className="back"
                onClick={() => {
                  setSelectedId(null)
                  setTrack(null)
                }}
              >
                ‹ {t.back}
              </button>
            </div>
            <div className="card-body">
              <h2 className="c-name">{title(selected)}</h2>
              <p className="c-state">
                <em data-st={statusOf(selected, positions.get(selected.id))}>
                  {stateText(statusOf(selected, positions.get(selected.id)), selected)}
                </em>
              </p>

              <Address
                latitude={positions.get(selected.id)?.latitude}
                longitude={positions.get(selected.id)?.longitude}
                lang={lang}
                enabled={maps}
              />

              <div className="stats">
                <div className="stat">
                  <b>{speedKmh(positions.get(selected.id))}</b>
                  <span>{t.speed}</span>
                </div>
                <div className="stat">
                  <b>{rpmText(positions.get(selected.id), lang)}</b>
                  <span>{t.rpm}</span>
                </div>
                <div className="stat">
                  <b>{odometerKm(positions.get(selected.id), lang)}</b>
                  <span>{t.odometer}</span>
                </div>
                <EnergyStat
                  position={positions.get(selected.id)}
                  tank={tankLiters(selected)}
                  lang={lang}
                  t={t}
                />
              </div>

              {identity.canManage &&
                tankMissing(positions.get(selected.id), tankLiters(selected)) && (
                  <p className="tank-note">{t.tankMissing}</p>
                )}

              <RefreshButton deviceId={selected.id} lang={lang} />

              {identity.canBlock && (
                <BlockButton
                  deviceId={selected.id}
                  deviceName={title(selected)}
                  blocked={isBlocked(positions.get(selected.id))}
                  lang={lang}
                  onDone={() => setSelectedId(selected.id)}
                />
              )}

              <div className="rows">
                <div className="kv">
                  <span>{t.voltageRow}</span>
                  <b>
                    {voltage(positions.get(selected.id), lang)} {t.voltage}
                  </b>
                </div>
                <div className="kv">
                  <span>{t.ignition}</span>
                  <b>{describeIgnition(positions.get(selected.id), t)}</b>
                </div>
                <div className="kv">
                  <span>{t.relay}</span>
                  <b data-st={isBlocked(positions.get(selected.id)) ? 'block' : undefined}>
                    {isBlocked(positions.get(selected.id)) ? t.relayRaised : t.relayClear}
                  </b>
                </div>
                <ChargingRow position={positions.get(selected.id)} t={t} />
                <div className="kv">
                  <span>{t.lastSeen}</span>
                  <b>{formatTime(selected.lastUpdate, lang)}</b>
                </div>
              </div>

              {maps && (
                <EtaButton lang={lang} ask={(from) => api.eta(selected.id, from)} />
              )}

              <RouteButton
                latitude={positions.get(selected.id)?.latitude ?? null}
                longitude={positions.get(selected.id)?.longitude ?? null}
                label={title(selected)}
                lang={lang}
                open={routeOpen}
                onOpen={() => setRouteOpen(true)}
                onClose={() => setRouteOpen(false)}
              />

              <ShareButton deviceId={selected.id} lang={lang} />

              <History
                device={selected}
                lang={lang}
                track={track}
                onTrack={setTrack}
                trackDays={trackDays}
              />
            </div>
          </>
        ) : (
          <>
            <div className="p-head">
              <div className="p-title-row">
                <h1 className="p-title">{t.appName}</h1>
                {/* Кнопка выхода превратилась в свою учётную запись: вход в
                    два шага человек настраивает сам, и место этому здесь, а не
                    в управлении, куда диспетчера не пускают. */}
                <button
                  className="p-user"
                  onClick={() => setProfileOpen(true)}
                  title={identity.name}
                >
                  {t.profile}
                </button>
              </div>

              {/* Поиск стоит над списком и виден всегда. Раньше он появлялся
                  с седьмой машины — но искать по госномеру хотят и в парке из
                  трёх, а появляющееся поле человек ищет глазами каждый раз. */}
              {tab === 'fleet' && (
                <input
                  className="search"
                  type="search"
                  value={query}
                  placeholder={t.searchPlate}
                  onChange={(e) => setQuery(e.target.value)}
                />
              )}
            </div>

            <div className="p-body">
              {tab === 'events' && <Events devices={devices} lang={lang} />}

              {tab === 'reports' && (
                <FleetReport devices={devices} positions={positions} lang={lang} />
              )}

              {tab === 'manage' && identity.canManage && (
                <Manage
                  identity={identity}
                  devices={devices}
                  positions={positions}
                  zones={zones}
                  lang={lang}
                  picking={{
                    active: pickingPoint,
                    point: pickedPoint,
                    start: () => {
                      setPickedPoint(null)
                      setPickingPoint(true)
                    },
                    clear: () => {
                      setPickedPoint(null)
                      setPickingPoint(false)
                    },
                  }}
                  onDevicesChanged={reload}
                  onZonesChanged={loadZones}
                />
              )}

              {tab === 'companies' && identity.serverAdmin && <Companies lang={lang} />}

              {tab === 'fleet' && failed && <p className="hint">{t.networkError}</p>}

              {tab === 'fleet' && !failed && devices.length === 0 && (
                <p className="hint">{t.noVehicles}</p>
              )}

              {tab === 'fleet' && devices.length > 0 && found.length === 0 && (
                <p className="hint">{t.nothingMatches}</p>
              )}

              {tab === 'fleet' &&
                found.map((device) => {
                  const status = statusOf(device, positions.get(device.id))
                  return (
                    <button
                      key={device.id}
                      className={'item' + (device.id === selectedId ? ' sel' : '')}
                      onClick={() => select(device.id)}
                    >
                      <span className="avatar" data-st={status}>
                        <span className="ring" />
                        <BrandMark device={device} />
                      </span>
                      <span>
                        <span className="i-name">{title(device)}</span>
                        <span className="i-sub">
                          {/* Марка и модель — перед состоянием: они не меняются,
                              и по ним машину узнают быстрее, чем по «стоит». */}
                          {describeVehicle(device) && (
                            <span className="i-make">{describeVehicle(device)} · </span>
                          )}
                          {status === 'block' ? (
                            <b>{t.blocked}</b>
                          ) : status === 'move' ? (
                            `${t.moving} · ${speedKmh(positions.get(device.id))} ${t.speed}`
                          ) : (
                            stateText(status, device)
                          )}
                        </span>
                      </span>
                      <span className="chev">›</span>
                    </button>
                  )
                })}
            </div>

            {/* Полоса вкладок внизу, как в телефонных приложениях: большой
                палец достаёт до неё, не перехватывая экран. На широком экране
                она перескакивает под заголовок — там низа панели не видно. */}
            <nav className="tabbar" role="tablist">
              {tabs.map((key) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                >
                  {tabIcon[key]}
                  <span>{tabName[key]}</span>
                </button>
              ))}
            </nav>
          </>
        )}
        {profileOpen && (
          <Profile
            theme={theme}
            onTheme={onTheme}
            identity={identity}
            lang={lang}
            onChanged={onIdentity}
            onSignOut={signOut}
            onClose={() => setProfileOpen(false)}
          />
        )}
        </div>
      </div>
    </>
  )
}

/** Ширина, ниже которой панель становится шторкой. Та же, что в стилях. */
const NARROW = '(max-width: 859px)'

/** Сколько длится переход к выбранному положению, секунды. */
const SETTLE_SECONDS = 0.3

/**
 * Сдвиг, после которого решается, чей это жест, пиксели.
 *
 * Он нарочно крошечный. Отменить прокрутку списка WebKit даёт только на самом
 * первом движении пальца: не успел — дальше решение не пересматривается,
 * и список поедет вместе со шторкой. Ждать здесь нельзя.
 */
const DECIDE_SLOP = 2

/**
 * Путь, после которого жест перестаёт быть нажатием, пиксели.
 *
 * Палец не стоит на месте и при обычном нажатии. Без этого зазора протяжка,
 * закончившаяся на строке списка, открывала бы машину, а дрожание пальца при
 * нажатии — съедалось бы как протяжка.
 */
const TAP_SLOP = 8

/**
 * Насколько давним может быть последнее движение, чтобы считать его броском,
 * миллисекунды. Человек мог довести панель, подумать и отпустить — это не
 * бросок, а «поставь сюда».
 */
const STALE_MS = 80

/** Что известно про идущий жест. */
type Gesture = {
  pointerId: number
  /** Где палец коснулся и какая была видимая часть: от них считается ход. */
  startY: number
  startShown: number
  /** Кому достался жест. Решается один раз и до конца жеста не меняется. */
  owner: 'unknown' | 'sheet' | 'list'
  /** Прокручиваемое место под пальцем, если палец лёг на него. */
  list: Element | null
  /** Самый дальний уход пальца от начала — по нему нажатие отличают от протяжки. */
  travel: number
  /** Где панель сейчас и как быстро идёт — пикселей в секунду, вверх плюс. */
  shown: number
  lastTime: number
  velocity: number
}

/**
 * Шторка на телефоне: три положения и протяжка пальцем за любое место панели.
 *
 * Сделана на своих обработчиках, а не на прокрутке браузера с прилипанием,
 * как было раньше. Причина не в красоте. Контейнер шторки обязан быть
 * `pointer-events: none`, иначе он забирает нажатия у карты под собой, — а в
 * WebKit такому контейнеру не отдают и прокрутку. Проверено на айфоне
 * владельца 7 сентября: шторка либо не тянулась вовсе, либо тянулась ценой
 * мёртвой карты и улетала выше крайнего положения, открывая под собой пустой
 * экран. Своя обработка ничего не оставляет на усмотрение движка.
 *
 * Считает не этот код, а sheet.ts: там та же арифметика без единого обращения
 * к разметке, и только её можно удержать тестом. Здесь — связь с пальцем.
 * Само ощущение жеста проверяется только рукой на живом телефоне.
 */
function useSheet(selectedId: number | null) {
  const sheetRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // Положения, текущая высота и ширина экрана живут в ref, а не в состоянии:
  // их читает обработчик касания на каждом кадре протяжки, и перерисовка ради
  // них была бы и лишней, и заметной.
  const detentsRef = useRef<Detents>([0, 0, 0])
  const shownRef = useRef(0)
  const detentRef = useRef<Detent>(0)
  const narrowRef = useRef(false)
  const boxRef = useRef(0)

  /** Поставить панель так, чтобы её было видно ровно на `shown` пикселей. */
  const apply = useCallback((shown: number, animate: boolean) => {
    const sheet = sheetRef.current
    if (!sheet || !narrowRef.current) return
    // Высота коробки берётся из запомненной: измерять её здесь нельзя.
    // apply зовут на каждом движении пальца, сразу после того, как прошлый
    // вызов записал transform, — и чтение размера заставило бы браузер
    // пересчитать разметку прямо посреди кадра. Обновляется она там, где
    // может измениться: при повороте, при изменении окна и в начале жеста
    // (браузер прячет и показывает свои полосы, и коробка меняется).
    const height = boxRef.current || sheet.getBoundingClientRect().height
    // Мгновенный переход при `prefers-reduced-motion: reduce` получается сам:
    // в стилях есть общее правило, гасящее переходы, и оно с !important.
    sheet.style.transition = animate
      ? `transform ${SETTLE_SECONDS}s cubic-bezier(0.32, 0.72, 0, 1)`
      : 'none'
    // Именно transform: он не вызывает пересчёт разметки и идёт на
    // видеокарте. На высоте или отступе протяжка дёргалась бы.
    sheet.style.transform = `translate3d(0, ${height - shown}px, 0)`
  }, [])

  const mark = useCallback((next: Detent) => {
    // Сверка не для красоты: mark зовут на каждом кадре протяжки.
    if (detentRef.current === next) return
    detentRef.current = next
    // Положение пишется прямо в узел, минуя состояние. Через состояние это
    // была бы перерисовка всего экрана — карты и списка вместе — и приходилась
    // бы она ровно на середину движения пальца, там, где рывок виден лучше
    // всего. Атрибут нужен только стилям (`touch-action` у панели и
    // `overflow` у списка), поэтому терять на нём кадры незачем.
    panelRef.current?.setAttribute('data-detent', String(next))
  }, [])

  /** Перевести шторку в положение `next`. */
  const settle = useCallback(
    (next: Detent, animate = true) => {
      mark(next)
      shownRef.current = detentsRef.current[next]
      apply(shownRef.current, animate)
    },
    [apply, mark],
  )

  /**
   * Перевести шторку в следующее положение — то, что делает нажатие на ручку.
   * Текущее положение читается из ref, а не из состояния: состояния у него
   * больше нет, и заводить его обратно ради одной кнопки значило бы вернуть
   * перерисовку всего экрана на середину протяжки.
   */
  const cycle = useCallback(() => {
    settle(nextDetent(detentRef.current))
  }, [settle])

  useEffect(() => {
    const sheet = sheetRef.current
    const panel = panelRef.current
    if (!sheet || !panel) return
    const media = window.matchMedia(NARROW)

    // Высоты трёх положений заданы в стилях через dvh и безопасную область
    // телефона. Перевести это в пиксели умеет только браузер, и спросить его
    // можно единственным способом — дать ему это померить.
    const measure = (): Detents => {
      const probe = document.createElement('div')
      probe.style.cssText =
        'position:absolute;top:0;left:0;width:0;visibility:hidden;pointer-events:none'
      document.body.append(probe)
      const read = (name: string) => {
        probe.style.height = `var(${name})`
        return probe.getBoundingClientRect().height
      }
      const detents: Detents = [
        read('--sheet-shut'),
        read('--sheet-mid'),
        read('--sheet-tall'),
      ]
      probe.remove()
      return detents
    }

    // Поворот телефона меняет и высоты положений, и то, шторка перед нами
    // или боковая панель. На широком экране сдвиг надо снять: transform на
    // обёртке сделал бы её точкой отсчёта для панели, и та уехала бы.
    const sync = () => {
      narrowRef.current = media.matches
      if (!media.matches) {
        sheet.style.transform = ''
        sheet.style.transition = ''
        return
      }
      detentsRef.current = measure()
      boxRef.current = sheet.getBoundingClientRect().height
      panel.setAttribute('data-detent', String(detentRef.current))
      shownRef.current = detentsRef.current[detentRef.current]
      apply(shownRef.current, false)
    }

    let gesture: Gesture | null = null
    // Ставится на отпускании, снимается первым же нажатием: гасить надо ровно
    // один щелчок — тот, которым кончилась протяжка.
    let swallowClick = false

    // Кому достаётся жест — шторке или списку внутри неё. Решается на первом
    // же заметном движении, когда известно направление, и до конца жеста не
    // пересматривается: иначе палец перескакивал бы между ними на середине.
    const decide = (y: number) => {
      if (!gesture || gesture.owner !== 'unknown') return
      const moved = y - gesture.startY
      if (Math.abs(moved) < DECIDE_SLOP) return
      // Правило вложенной прокрутки решает спор шторки со списком. Там, где
      // спорить не с кем — заголовок, ручка, полоса вкладок, — шторка берёт
      // жест себе: иначе за раскрытой шторкой с прокрученным списком её
      // нечем было бы закрыть.
      const mine =
        gesture.list === null ||
        shouldDragSheet(
          detentRef.current,
          moved < 0 ? 'up' : 'down',
          gesture.list.scrollTop,
        )
      gesture.owner = mine ? 'sheet' : 'list'
      if (!mine) return
      try {
        // Палец может уйти с панели и даже за край экрана — движение всё
        // равно останется нашим.
        panel.setPointerCapture(gesture.pointerId)
      } catch {
        // Указателя уже нет — значит, и вести нечего.
      }
    }

    const onDown = (event: PointerEvent) => {
      if (!narrowRef.current || gesture) return
      if (event.pointerType === 'mouse' && event.button !== 0) return
      swallowClick = false
      // Один замер на весь жест: дальше по нему будут считаться все кадры.
      boxRef.current = sheet.getBoundingClientRect().height
      // Прокручиваемых мест в панели два: список машин и карточка выбранной.
      // Какое из них под пальцем, спрашивается сейчас, а не при первом
      // движении: к тому времени под пальцем может оказаться уже другое.
      const target = event.target
      gesture = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startShown: shownRef.current,
        owner: 'unknown',
        list:
          target instanceof Element
            ? target.closest('.p-body, .card-body')
            : null,
        travel: 0,
        shown: shownRef.current,
        lastTime: event.timeStamp,
        velocity: 0,
      }
    }

    const onMove = (event: PointerEvent) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return
      decide(event.clientY)
      gesture.travel = Math.max(gesture.travel, Math.abs(event.clientY - gesture.startY))
      if (gesture.owner !== 'sheet') return
      const shown = withResistance(
        gesture.startShown + (gesture.startY - event.clientY),
        detentsRef.current,
      )
      // Скорость считается по последнему заметному промежутку: на слишком
      // коротком она вырождается в шум от дрожания пальца.
      const elapsed = event.timeStamp - gesture.lastTime
      if (elapsed >= 8) {
        gesture.velocity = ((shown - gesture.shown) / elapsed) * 1000
        gesture.lastTime = event.timeStamp
      }
      gesture.shown = shown
      shownRef.current = shown
      apply(shown, false)
      mark(detentFor(shown, detentsRef.current))
    }

    // Отдельно от pointermove и намеренно не passive: отменить прокрутку
    // списка можно только отсюда, и только на первом же движении — дальше
    // WebKit своё решение не пересматривает. Какое из двух событий придёт
    // первым, нигде не оговорено, поэтому спрашивают оба, а отвечает decide
    // один раз за жест.
    const onTouchMove = (event: TouchEvent) => {
      if (!gesture) return
      const touch = event.touches[0]
      if (touch) decide(touch.clientY)
      if (gesture.owner === 'sheet') event.preventDefault()
    }

    const onUp = (event: PointerEvent) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return
      const done = gesture
      gesture = null
      if (done.owner !== 'sheet') return
      // Нажатие после протяжки — не нажатие: палец отпускают то на строке
      // списка, то на ручке, и открывать по этому машину нельзя.
      swallowClick = done.travel >= TAP_SLOP
      const still = event.timeStamp - done.lastTime > STALE_MS
      settle(
        projectRelease(done.shown, still ? 0 : done.velocity, detentsRef.current),
      )
    }

    // Жест отобрали (звонок, системный жест) — доводим до ближайшего
    // положения. Бросок тут не считается: движения, которое можно было бы
    // назвать броском, не было.
    const onCancel = (event: PointerEvent) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return
      const done = gesture
      gesture = null
      if (done.owner === 'sheet') {
        settle(detentFor(done.shown, detentsRef.current))
      }
    }

    const onClick = (event: MouseEvent) => {
      if (!swallowClick) return
      swallowClick = false
      event.stopPropagation()
      event.preventDefault()
    }

    sync()
    media.addEventListener('change', sync)
    window.addEventListener('resize', sync)
    // Полосы браузера прячутся и показываются, меняя видимую область, а
    // window.resize при этом не всегда приходит.
    window.visualViewport?.addEventListener('resize', sync)
    // Отпускают где угодно, в том числе мимо панели, — поэтому на окне.
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onCancel)
    panel.addEventListener('pointerdown', onDown)
    panel.addEventListener('pointermove', onMove)
    panel.addEventListener('touchmove', onTouchMove, { passive: false })
    panel.addEventListener('click', onClick, true)
    return () => {
      media.removeEventListener('change', sync)
      window.removeEventListener('resize', sync)
      window.visualViewport?.removeEventListener('resize', sync)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onCancel)
      panel.removeEventListener('pointerdown', onDown)
      panel.removeEventListener('pointermove', onMove)
      panel.removeEventListener('touchmove', onTouchMove)
      panel.removeEventListener('click', onClick, true)
    }
  }, [apply, mark, settle])

  /**
   * Поднять свёрнутую шторку до среднего — то, что делает выбор машины.
   * Иначе карточка открылась бы за краем экрана, и человек решил бы, что
   * нажатие не сработало.
   */
  const raise = useCallback(() => {
    if (!narrowRef.current || detentRef.current !== 0) return
    settle(1)
  }, [settle])

  // Тем же кончаются выборы, сделанные не нажатием в списке, — например,
  // возврат к машине после снятия блокировки.
  useEffect(() => {
    if (selectedId === null) return
    raise()
  }, [selectedId, raise])

  /**
   * Сколько экрана закрыто своим прямо сейчас. Нужно карте: машину она
   * ставит в середину того, что осталось.
   *
   * Спрашивается ровно в тот миг, когда карта собирается лететь, и потому
   * может позволить себе замер разметки — в отличие от `apply`, который
   * зовут на каждом кадре протяжки. Высота шторки при этом уже не текущая,
   * а та, к которой шторка едет: `settle` записывает её сразу, до конца
   * своего перехода, и карта целится туда же, куда и шторка.
   */
  const inset = useCallback((): Inset => {
    if (narrowRef.current) return { left: 0, bottom: shownRef.current }
    // На широком экране мерка снимается с самой панели, а не задаётся числом:
    // её ширина живёт в стилях и там же меняется.
    const box = panelRef.current?.getBoundingClientRect()
    return { left: box ? box.right : 0, bottom: 0 }
  }, [])

  return { sheetRef, panelRef, cycle, raise, inset }
}

/**
 * Значок машины: буквы марки, если её видно по имени, иначе обычный фургон.
 *
 * Буквы, а не логотип: логотип марки — товарный знак, и свободной его версии
 * не бывает. Подробнее — в brand.ts.
 */
function BrandMark({ device }: { device: Device }) {
  // Сначала паспорт: марку задали руками, и гадать по имени незачем.
  // Угадывание остаётся для машин, заведённых до появления паспорта.
  const stated = passportOf(device).brand
  const brand = stated ? { short: '', size: 10, art: stated } : brandOf(device.name)
  if (!brand) return <TruckIcon />
  if (brand.art) {
    // Разметка своя, из brandArt: наша константа, а не чужой ввод.
    return <span className="art" dangerouslySetInnerHTML={{ __html: ART[brand.art] }} />
  }
  return (
    <span className="brand" style={{ fontSize: brand.size }}>
      {brand.short}
    </span>
  )
}

/**
 * Плитка с уровнем топлива или заряда.
 *
 * Отдельным куском, а не строкой в разметке: показывать её можно только когда
 * значение есть. У машины без шины CAN его не будет вовсе, и пустая плитка с
 * прочерком заняла бы место, ничего не сообщив.
 *
 * Литры (AVL 84) — отдельной строкой под процентами: рядом с ними в четверть
 * ширины панели они не помещаются, а в подписи рвутся посередине. У заряда
 * литров не бывает.
 */
function EnergyStat({
  position,
  tank,
  lang,
  t,
}: {
  position: Position | undefined
  tank: number | null
  lang: Lang
  t: ReturnType<typeof dict>
}) {
  const energy = energyLevel(position)
  if (!energy) return null
  const volume = energy.kind === 'fuel' ? fuelVolume(position, tank) : null
  return (
    <div className="stat">
      <b>{num(energy.percent, lang)}%</b>
      {volume !== null && (
        <small>
          {volume.estimated && '≈ '}
          {num(volume.liters, lang)} {t.unitLiter}
        </small>
      )}
      <span>{energy.kind === 'fuel' ? t.fuel : t.charge}</span>
    </div>
  )
}

/** Обороты целым числом; без шины CAN — прочерк, как у пробега. */
function rpmText(position: Position | undefined, lang: Lang): string {
  const value = rpm(position)
  return value === null ? '—' : num(value, lang)
}

/** Состояние зарядки — только у электрических машин, у остальных его нет. */
function ChargingRow({
  position,
  t,
}: {
  position: Position | undefined
  t: ReturnType<typeof dict>
}) {
  const state = chargingState(position)
  if (state === undefined) return null
  const text = state === 'charging' ? t.chargeOn : state === 'plugged' ? t.chargePlugged : t.chargeOff
  return (
    <div className="kv">
      <span>{t.chargeState}</span>
      <b>{text}</b>
    </div>
  )
}

function describeIgnition(position: Position | undefined, t: ReturnType<typeof dict>): string {
  const state = ignitionOn(position)
  if (state === undefined) return t.unknown
  return state ? t.on : t.off
}

function formatTime(iso: string | null, lang: Lang): string {
  return iso ? clock(iso, lang) : '—'
}
